import { useQuery } from '@apollo/client';
import { generateItemsWithPromosAmounts, usePaginationSearch, useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { BreedsGetBreedsQuery, BreedsGetBreedsQueryVariables } from '@gql/graphql';
import { useMemo } from 'react';
import { PAGINATED_SIZE } from '../constants';
import { useSearch } from '../useSearch';
import { useBreedsWhereBuilder } from './useBreedWhereBuilder';

export const BREEDS_GET_BREEDS = graphql(`
  query BreedsGetBreeds(
    $orderBy: [DogBreedOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $skip: Int
    $where: DogBreedWhereInput
    $breedCardInfoTranslationsWhere: DogBreedTranslationWhereInput
  ) {
    findManyDogBreed(orderBy: $orderBy, skip: $skip, take: $take, where: $where) {
      ...BreedCardInfo
    }
    aggregateDogBreed(where: $where) {
      _count {
        _all
      }
    }
  }
`);

export const useGetBreeds = () => {
  const { breedsWhereBuilder } = useBreedsWhereBuilder();
  const { valuesFromSearch } = useSearch();
  const { language } = useRouter();
  const { currentPage } = usePaginationSearch();

  const variables: BreedsGetBreedsQueryVariables = useMemo(() => {
    return {
      where: breedsWhereBuilder(valuesFromSearch),
      take: generateItemsWithPromosAmounts(PAGINATED_SIZE).itemAmount,
      skip: generateItemsWithPromosAmounts(PAGINATED_SIZE).itemAmount * currentPage,
      orderBy: [{ name: 'asc' }],
      breedCardInfoTranslationsWhere: { language: { equals: language } },
    } as BreedsGetBreedsQueryVariables;
  }, [breedsWhereBuilder, currentPage, language, valuesFromSearch]);

  const { data, error, loading, fetchMore, refetch } = useQuery<
    BreedsGetBreedsQuery,
    BreedsGetBreedsQueryVariables
  >(BREEDS_GET_BREEDS, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  return {
    breeds: data?.findManyDogBreed ?? [],
    // eslint-disable-next-line no-underscore-dangle
    breedsCount: data?.aggregateDogBreed?._count?._all ?? 0,
    breedsError: error,
    breedsLoading: loading,
    breedsVariables: variables,
    breedsFetchMore: fetchMore,
    breedsRefetch: refetch,
  };
};
